import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Banknote, ChevronDown, Phone, Wallet } from 'lucide-react';
import { useDashboardUi } from '@/components/layout/UserLayout';
import { PinVerificationModal } from '@/components/user/PinVerificationModal';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import {
  AIRTIME_AMOUNT_PRESETS,
  AIRTIME_NETWORKS,
  detectAirtimeNetwork,
  getAirtimeNetworkMeta,
} from '@/constants/airtimeNetworks';
import { useUserAuth } from '@/context/UserAuthContext';
import { airtimeService, getApiErrorMessage } from '@/services';
import type { AirtimeNetwork, AirtimeNetworkOption } from '@/types/airtime';
import { cn } from '@/utils/cn';
import { notify } from '@/utils/notify';

export function AirtimePage() {
  const { user, persistUser } = useUserAuth();
  const { wallet, loadWallet } = useDashboardUi();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [networkOptions, setNetworkOptions] = useState<AirtimeNetworkOption[]>([]);
  const [providerReady, setProviderReady] = useState(true);
  const [loadingNetworks, setLoadingNetworks] = useState(true);

  const [selectedNetwork, setSelectedNetwork] = useState<AirtimeNetwork>('MTN');
  const [networkManual, setNetworkManual] = useState(false);
  const [showNetworkDropdown, setShowNetworkDropdown] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedAmount, setSelectedAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState('');
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [amountToPay, setAmountToPay] = useState<number | null>(null);
  const [discountPercent, setDiscountPercent] = useState(0);

  const [showPinModal, setShowPinModal] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  const selectedMeta = getAirtimeNetworkMeta(selectedNetwork);

  const faceValue = useMemo(() => {
    if (customAmount.trim()) {
      const parsed = Number(customAmount);
      return Number.isFinite(parsed) && parsed >= 50 ? Math.round(parsed) : null;
    }
    return selectedAmount;
  }, [customAmount, selectedAmount]);

  const walletBalance = wallet?.balance ?? user?.walletBalance ?? 0;

  const loadNetworks = useCallback(async () => {
    setLoadingNetworks(true);
    try {
      const res = await airtimeService.getNetworks();
      const data = res.data;
      setNetworkOptions(data?.networks ?? []);
      setProviderReady(Boolean(data?.configured));
      if (data?.networks?.[0]) {
        setSelectedNetwork(data.networks[0].network);
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load airtime settings'));
    } finally {
      setLoadingNetworks(false);
    }
  }, []);

  const loadQuote = useCallback(async () => {
    if (!faceValue) {
      setAmountToPay(null);
      return;
    }
    setQuoteLoading(true);
    try {
      const res = await airtimeService.getQuote(selectedNetwork, faceValue);
      const quote = res.data?.quote;
      setAmountToPay(quote?.amountToPay ?? faceValue);
      setDiscountPercent(quote?.discountPercent ?? 0);
    } catch {
      setAmountToPay(faceValue);
      setDiscountPercent(0);
    } finally {
      setQuoteLoading(false);
    }
  }, [faceValue, selectedNetwork]);

  useEffect(() => {
    void loadNetworks();
  }, [loadNetworks]);

  useEffect(() => {
    void loadQuote();
  }, [loadQuote]);

  useEffect(() => {
    function onClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowNetworkDropdown(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  function handlePhoneChange(value: string) {
    const numeric = value.replace(/\D/g, '').slice(0, 11);
    setPhoneNumber(numeric);
    if (numeric.length >= 4 && !networkManual) {
      const detected = detectAirtimeNetwork(numeric);
      if (detected) setSelectedNetwork(detected);
    }
  }

  function handleNetworkPick(network: AirtimeNetwork) {
    setSelectedNetwork(network);
    setNetworkManual(true);
    setShowNetworkDropdown(false);
  }

  const activeNetwork = networkOptions.find((n) => n.network === selectedNetwork);
  const canPurchase =
    providerReady &&
    Boolean(activeNetwork?.isActive) &&
    faceValue &&
    faceValue >= 50 &&
    phoneNumber.length === 11 &&
    user?.pinEnabled &&
    (networkManual || Boolean(detectAirtimeNetwork(phoneNumber)));

  async function handlePurchase(pin: string) {
    if (!faceValue) return;
    setPurchasing(true);
    try {
      const res = await airtimeService.purchase({
        network: selectedNetwork,
        phoneNumber,
        amount: faceValue,
        pin,
      });
      if (res.data?.user) persistUser(res.data.user);
      await loadWallet();
      notify.success(res.message || 'Airtime purchased successfully');
      setShowPinModal(false);
      setPhoneNumber('');
      setCustomAmount('');
      setNetworkManual(false);
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Airtime purchase failed'));
    } finally {
      setPurchasing(false);
    }
  }

  if (loadingNetworks) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader label="Loading airtime" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-brand-navy sm:text-3xl">Buy Airtime</h1>
        <p className="mt-1 text-sm text-brand-muted sm:text-base">
          Instant VTU for MTN, Airtel, Glo, and 9mobile
        </p>
      </div>

      {!providerReady && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Airtime is temporarily unavailable. Please try again later.
        </div>
      )}

      {!user?.pinEnabled && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Set your transaction PIN in Security before buying airtime.
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-gray-200 p-5 shadow-card sm:p-6 lg:p-8">
          <h2 className="mb-6 hidden text-lg font-bold text-brand-navy sm:block">Purchase Airtime</h2>

          <div className="space-y-6">
            <div>
              <label htmlFor="airtime-phone" className="mb-2 block text-sm font-medium text-brand-navy">
                Phone number
              </label>
              <div className="flex items-center gap-3">
                <div ref={dropdownRef} className="relative shrink-0">
                  <button
                    type="button"
                    onClick={() => setShowNetworkDropdown((open) => !open)}
                    className={cn(
                      'flex h-14 w-14 items-center justify-center rounded-full border-2 bg-white transition hover:shadow-md',
                      showNetworkDropdown ? 'border-primary ring-2 ring-primary/20' : 'border-gray-200',
                    )}
                    aria-label="Select network"
                  >
                    <img
                      src={selectedMeta.logo}
                      alt={selectedMeta.label}
                      className="h-9 w-9 object-contain"
                    />
                  </button>
                  <ChevronDown
                    className={cn(
                      'pointer-events-none absolute -right-1 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-navy transition',
                      showNetworkDropdown && 'rotate-180',
                    )}
                  />

                  {showNetworkDropdown && (
                    <div className="absolute left-0 top-full z-20 mt-2 min-w-[220px] rounded-xl border border-gray-200 bg-white p-4 shadow-card">
                      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-brand-muted">
                        Select network
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        {AIRTIME_NETWORKS.map((network) => (
                          <button
                            key={network.id}
                            type="button"
                            onClick={() => handleNetworkPick(network.id)}
                            className={cn(
                              'flex flex-col items-center gap-2 rounded-xl border-2 p-3 transition hover:scale-[1.02]',
                              selectedNetwork === network.id
                                ? 'border-primary bg-primary/5 shadow-sm'
                                : 'border-gray-100 hover:border-gray-200',
                            )}
                          >
                            <img src={network.logo} alt={network.label} className="h-10 w-10 object-contain" />
                            <span className="text-xs font-semibold text-brand-navy">{network.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="relative min-w-0 flex-1">
                  <Phone className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-brand-muted" />
                  <input
                    id="airtime-phone"
                    type="tel"
                    inputMode="numeric"
                    value={phoneNumber}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    placeholder="Enter phone number"
                    maxLength={11}
                    className="w-full rounded-xl border border-gray-200 py-3.5 pl-12 pr-4 text-base text-brand-navy focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>

              {phoneNumber.length > 0 && phoneNumber.length !== 11 && (
                <p className="mt-2 text-sm text-red-600">Phone number must be exactly 11 digits</p>
              )}
              {phoneNumber.length === 11 && !networkManual && !detectAirtimeNetwork(phoneNumber) && (
                <p className="mt-2 text-sm text-amber-700">
                  Network not detected. Tap the logo to choose your carrier.
                </p>
              )}
            </div>

            <div>
              <p className="mb-3 text-sm font-medium text-brand-navy">Select amount</p>
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {AIRTIME_AMOUNT_PRESETS.map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => {
                      setSelectedAmount(amount);
                      setCustomAmount('');
                    }}
                    className={cn(
                      'rounded-xl border-2 px-2 py-3 text-sm font-semibold transition sm:py-4 sm:text-base',
                      !customAmount && selectedAmount === amount
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-gray-200 text-brand-navy hover:border-primary/40',
                    )}
                  >
                    ₦{amount.toLocaleString()}
                  </button>
                ))}
              </div>

              <div className="mt-4">
                <label htmlFor="custom-amount" className="mb-2 block text-sm font-medium text-brand-navy">
                  Or enter custom amount
                </label>
                <div className="relative">
                  <Banknote className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-brand-muted" />
                  <input
                    id="custom-amount"
                    type="number"
                    min={50}
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value.replace(/\D/g, ''))}
                    placeholder="Min ₦50"
                    className="w-full rounded-xl border border-gray-200 py-3.5 pl-12 pr-4 text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-100 bg-brand-surface/60 p-4 lg:hidden">
              <div className="flex items-center justify-between text-sm">
                <span className="text-brand-muted">You pay</span>
                <span className="text-lg font-bold text-primary">
                  {quoteLoading || amountToPay == null ? '…' : `₦${amountToPay.toLocaleString()}`}
                </span>
              </div>
            </div>

            <Button
              fullWidth
              size="lg"
              className="py-3.5 text-base"
              disabled={!canPurchase}
              onClick={() => setShowPinModal(true)}
            >
              {faceValue ? `Buy ₦${faceValue.toLocaleString()} Airtime` : 'Buy airtime'}
            </Button>
          </div>
        </Card>

        <Card className="border-gray-200 p-5 shadow-card sm:p-6 lg:p-8">
          <div className="mb-6 flex items-center gap-2 border-b border-gray-100 pb-4">
            <Wallet className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-bold text-brand-navy">Order Summary</h2>
          </div>

          <div className="space-y-0">
            <div className="flex items-center gap-4 border-b border-gray-100 py-4">
              <img src={selectedMeta.logo} alt={selectedMeta.label} className="h-12 w-12 object-contain" />
              <div>
                <p className="font-semibold text-brand-navy">{selectedMeta.label}</p>
                <p className="text-sm text-brand-muted">Network</p>
              </div>
            </div>

            <div className="border-b border-gray-100 py-4">
              <p className="font-semibold text-brand-navy">{phoneNumber || '—'}</p>
              <p className="text-sm text-brand-muted">Phone number</p>
            </div>

            <div className="border-b border-gray-100 py-4">
              <p className="font-semibold text-brand-navy">
                {faceValue ? `₦${faceValue.toLocaleString()}` : '—'}
              </p>
              <p className="text-sm text-brand-muted">Face value</p>
            </div>

            <div className="border-b border-gray-100 py-4">
              <p className="font-semibold text-secondary">{discountPercent}%</p>
              <p className="text-sm text-brand-muted">Your discount</p>
            </div>

            <div className="flex items-end justify-between py-5">
              <div>
                <p className="font-semibold text-brand-navy">Total</p>
                <p className="text-sm text-brand-muted">Including all charges</p>
              </div>
              <p className="text-2xl font-bold text-primary">
                {quoteLoading || amountToPay == null ? '…' : `₦${amountToPay.toLocaleString()}`}
              </p>
            </div>
          </div>

          <div className="mt-2 rounded-xl bg-brand-surface px-4 py-3 text-sm">
            <div className="flex justify-between gap-3">
              <span className="text-brand-muted">Wallet balance</span>
              <span className="font-semibold text-brand-navy">₦{walletBalance.toLocaleString()}</span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-brand-muted">
              {user?.userType === 'agent'
                ? 'Agent discount is applied automatically.'
                : 'Subscriber pricing applies. Upgrade to agent for better rates.'}
            </p>
          </div>
        </Card>
      </div>

      <PinVerificationModal
        open={showPinModal}
        onClose={() => setShowPinModal(false)}
        onConfirm={(pin) => void handlePurchase(pin)}
        loading={purchasing}
        title="Confirm airtime purchase"
        description={
          faceValue && amountToPay != null
            ? `Pay ₦${amountToPay.toLocaleString()} for ₦${faceValue.toLocaleString()} ${selectedMeta.label} airtime.`
            : undefined
        }
      />
    </div>
  );
}
